import { computed, type Ref } from 'vue';
import type { UserStepProgress } from 'types/course/step';
import { calculateStepScore } from '../views/StepView/utilities/stepScore';

/** Any stack item: only its id is read here. */
export type ProgressStackItem = { id?: number | string };

type StepResults = UserStepProgress['stack']['results'];

export interface UseStepProgressOptions<T extends ProgressStackItem> {
  orderedStackSource: Readonly<Ref<T[]>>;
  currentStepResults: Readonly<Ref<StepResults>>;
  currentParsedProgress: Readonly<Ref<UserStepProgress | null>>;
  repoUserStep: Readonly<Ref<{ completion_date?: string | null } | null>>;
  /** Explicit id only (BUG-5): answered = has a result that is not incomplete. */
  isAnswered: (id: number | string | undefined) => boolean;
}

/**
 * Where the student is in the stack and what the results add up to
 * (moved verbatim from useStep).
 */
export function useStepProgress<T extends ProgressStackItem>({
  orderedStackSource,
  currentStepResults,
  currentParsedProgress,
  repoUserStep,
  isAnswered,
}: UseStepProgressOptions<T>) {
  const answeredArray = computed(() => {
    if (!currentStepResults.value) return [];
    return Object.keys(currentStepResults.value).reduce<string[]>(
      (prev, curr) =>
        currentStepResults.value?.[curr]?.incomplete ? prev : [...prev, curr],
      [],
    );
  });

  const isCompletedStep = computed(() => {
    if (repoUserStep.value?.completion_date) return true;
    if (orderedStackSource.value?.length === 0) return false; // No questions

    return answeredArray.value?.length === orderedStackSource.value?.length;
  });

  const currentStackItem = computed(() => {
    if (!orderedStackSource.value) return null;
    return orderedStackSource.value?.[stackIndex.value];
  });

  const stackIndex = computed(() => {
    if (!orderedStackSource.value) return 0;
    let index = 0;
    while (
      index < orderedStackSource.value.length &&
      isAnswered(orderedStackSource.value[index]?.id)
    ) {
      index++;
    }
    return index;
  });

  const errorCount = computed(() => {
    if (!currentStepResults?.value) return 0;
    return (
      Object.values(currentStepResults.value).filter(
        (v) => !v?.tries?.[0]?.isCorrect,
      )?.length ?? 0
    );
  });

  const correctCount = computed(() => {
    if (!currentStepResults?.value) return 0;
    return (
      Object.values(currentStepResults.value).filter(
        (v) => !!v?.tries?.[0]?.isCorrect,
      )?.length ?? 0
    );
  });

  const timeAccumulator = computed(() => {
    if (!currentStepResults?.value) return 0;
    return Object.values(currentStepResults.value).reduce((prev, curr) => {
      if (!curr?.tries) return prev;
      return prev + curr.tries.reduce((prev, curr) => prev + curr.duration, 0);
    }, 0);
  });

  const score = computed(() => {
    const length = currentParsedProgress.value?.stack?.length;
    return calculateStepScore(correctCount.value, Number(length));
  });

  const stepProgressPercent = computed(() => {
    if (!orderedStackSource.value?.length) return 0;
    return answeredArray.value.length / orderedStackSource.value.length;
  });

  const stepProgressLabel = computed(() => {
    if (!orderedStackSource.value) return '';
    return `${answeredArray.value.length} / ${orderedStackSource.value.length}`;
  });

  const isQuestionAnswered = (questionId?: number): boolean =>
    isAnswered(questionId ?? currentStackItem.value?.id);

  return {
    stackIndex,
    currentStackItem,
    isCompletedStep,
    errorCount,
    correctCount,
    timeAccumulator,
    score,
    stepProgressPercent,
    stepProgressLabel,
    isQuestionAnswered,
  };
}

export type UseStepProgressReturn<T extends ProgressStackItem> = ReturnType<
  typeof useStepProgress<T>
>;
